import { useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
const filterChips = ["ALL", "CYBERSECURITY", "VPN", "PRIVACY", "TECH"];
const coverPalettes = [{
  bg: "bg-slate-900",
  text: "text-slate-50",
  rule: "bg-orange-500",
  accent: "text-orange-500"
}, {
  bg: "bg-orange-600",
  text: "text-white",
  rule: "bg-white",
  accent: "text-white"
}, {
  bg: "bg-slate-800",
  text: "text-slate-50",
  rule: "bg-orange-500",
  accent: "text-orange-500"
}, {
  bg: "bg-slate-900",
  text: "text-slate-50",
  rule: "bg-orange-500",
  accent: "text-orange-500"
}, {
  bg: "bg-orange-600",
  text: "text-white",
  rule: "bg-white",
  accent: "text-white"
}, {
  bg: "bg-slate-800",
  text: "text-slate-50",
  rule: "bg-orange-500",
  accent: "text-orange-500"
}];
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function estimateReadingTime(content?: string, excerpt?: string): string {
  const text = stripHtml(content) || stripHtml(excerpt) || "";
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(3, Math.ceil(words / 200));
  return `${minutes} MIN READ`;
}
function RecentChaptersGrid({
  postsPerPage
}: {
  postsPerPage: number;
}) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="1156" className="grid grid-cols-1 gap-6">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-video bg-slate-200 border border-slate-300 animate-pulse rounded-lg" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-slate-300 bg-slate-100 p-10 text-center rounded-lg">
        <p className="font-default italic text-foreground/80 mb-4">
          The security archives are currently unavailable.
        </p>
        <Button onClick={refetch} variant="outline" className="font-default uppercase tracking-[0.16em] text-xs rounded-lg border-foreground text-foreground hover:bg-foreground hover:text-background">
          Retry Connection
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-slate-300 bg-slate-100 p-16 text-center rounded-lg">
        <p className="font-default uppercase tracking-[0.18em] text-xs text-orange-600 mb-3">
          § V.000 / GENIX — NO UPDATES
        </p>
        <p className="font-default italic text-lg text-foreground/80">
          No blogs found at this time.
        </p>
      </div>;
  }
  const displayedPosts = posts.slice(0, postsPerPage);
  return <div className="grid grid-cols-1 gap-6">
      {displayedPosts.map((post, i) => {
      const palette = coverPalettes[i % coverPalettes.length];
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "SECURITY";
      const indexLabel = `V.${String(i + 1).padStart(3, "0")}${String.fromCharCode(97 + i % 3)}`;
      const editionNo = `No. ${String(i + 1).padStart(2, "0")}`;
      const cleanTitle = stripHtml(post.title);
      return <article data-index={i} key={post.id} className="group bg-card border border-border rounded-lg overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg">
            <Link to={post.link || "#"} className="block">
              <div className="flex flex-col md:flex-row w-full">
                <div className={`relative aspect-video md:w-[30%] md:aspect-auto ${post.featuredImage ? "bg-slate-900" : palette.bg} ${palette.text} overflow-hidden transition-transform duration-300 ease-out group-hover:-rotate-1 shrink-0 flex flex-col justify-between p-6`}>
                  {post.featuredImage ? <img src={post.featuredImage} alt={cleanTitle} className="absolute inset-0 w-full h-full object-cover" loading="lazy" /> : <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay" style={{
                backgroundImage: "radial-gradient(circle at 20% 30%, hsl(0 0% 100%) 0%, transparent 2%), radial-gradient(circle at 70% 60%, hsl(0 0% 100%) 0%, transparent 1.5%), radial-gradient(circle at 40% 80%, hsl(0 0% 100%) 0%, transparent 2%)"
              }} />}

                  <div className="flex items-start justify-between relative z-10">
                    <span className={`font-default text-[0.7rem] uppercase tracking-[0.18em] ${post.featuredImage ? "text-orange-400 bg-black/40 px-2 py-0.5 rounded-md" : palette.accent}`}>
                      § {indexLabel}
                    </span>
                    <span className={`font-default text-[0.65rem] uppercase tracking-[0.16em] ${post.featuredImage ? "text-white bg-black/40 px-2 py-0.5 rounded-md" : palette.text} opacity-90`}>
                      {category}
                    </span>
                  </div>

                  <div className="my-auto py-6 relative z-10">
                    <span className={`font-default text-[0.65rem] uppercase tracking-[0.18em] ${post.featuredImage ? "text-orange-400 bg-black/40 px-2 py-0.5 rounded-md" : palette.accent}`}>
                      {editionNo}
                    </span>
                  </div>

                  <div className="flex items-end justify-between relative z-10">
                    <span className={`font-default text-[0.65rem] uppercase tracking-[0.18em] ${post.featuredImage ? "text-white bg-black/40 px-2 py-0.5 rounded-md" : palette.text} opacity-90`}>
                      VPNGENIX
                    </span>
                  </div>
                </div>

                <div className="flex-1 p-6 md:p-8 flex flex-col justify-between bg-card">
                  <div>
                    <div className="flex items-center gap-3 font-default text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground mb-3">
                      <span>{formatDate(post.date)}</span>
                      <span className="h-1 w-1 rounded-lg bg-orange-500" />
                      <span>{category}</span>
                    </div>

                    <h3 className="font-default font-semibold leading-[1.12] tracking-[-0.02em] text-foreground mb-4" style={{
                  fontSize: "clamp(1.25rem, 2vw, 1.75rem)"
                }}>
                      {cleanTitle}
                    </h3>

                    <p className="font-default text-sm text-muted-foreground line-clamp-2">
                      {stripHtml(post.excerpt) || stripHtml(post.content)}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center">
                      <span className={`h-px w-12 ${palette.rule} transition-all duration-300 ease-out group-hover:w-20`} />
                      <span className={`ml-2 h-1.5 w-1.5 rounded-lg ${palette.rule}`} />
                    </div>
                    <span className="font-default text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
                      {estimateReadingTime(post.content, post.excerpt)}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </article>;
    })}
    </div>;
}
export default function LatestSecurityInsights() {
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const wp_query_params = {
    post_type: "post",
    per_page: showAll ? 100 : 6,
    order: "desc",
    orderby: "date",
    search: searchQuery
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latestsecurityinsights" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 12% 18%, hsl(221 30% 15%) 0%, transparent 1.5%), radial-gradient(circle at 78% 32%, hsl(221 30% 15%) 0%, transparent 1.2%), radial-gradient(circle at 35% 70%, hsl(221 30% 15%) 0%, transparent 1.8%), radial-gradient(circle at 88% 85%, hsl(221 30% 15%) 0%, transparent 1.4%), radial-gradient(circle at 50% 50%, hsl(221 30% 15%) 0%, transparent 1%)"
    }} />

      <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <p className="font-default text-[0.75rem] uppercase tracking-[0.18em] text-orange-600 mb-6">
              § V.001 / VPNGENIX — LATEST BLOGS
            </p>
            <h2 className="font-default font-bold text-foreground leading-[1.02] tracking-[-0.02em]" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)"
          }}>
              Securing your <em className="italic">digital</em> footprint.
            </h2>
            <div className="mt-8 flex items-center">
              <span className="h-px w-16 bg-foreground" />
              <span className="ml-2 h-1.5 w-1.5 rounded-lg bg-orange-600" />
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-default text-foreground/80 leading-[1.55]" style={{
            fontSize: "clamp(0.95rem, 1.1vw, 1.0625rem)"
          }}>
              Expert analysis on cybersecurity trends, privacy protection, and the latest VPN technologies to keep your data safe in an evolving digital landscape.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {filterChips.map((chip, i) => <Badge data-index={i} key={chip} variant="outline" className="font-default text-[0.65rem] uppercase tracking-[0.16em] rounded-lg border border-orange-600 text-orange-600 bg-transparent px-2.5 py-1 cursor-pointer hover:bg-orange-600 hover:text-white transition-colors">
                  {chip}
                </Badge>)}
            </div>
          </div>
        </div>

        <div className="mb-12 max-w-md">
          <Input type="text" placeholder="Search Latest Topics" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="rounded-lg h-12 bg-card border-border text-foreground font-default" />
        </div>

        <PostsProvider wp_query={wp_query}>
          <RecentChaptersGrid postsPerPage={showAll ? 100 : 6} />
          
          <div className="mt-16 text-center">
            <Button onClick={() => setShowAll(!showAll)} className="bg-orange-600 text-white hover:bg-orange-700 rounded-lg px-8 py-6 font-default uppercase tracking-[0.16em] text-sm">
              {showAll ? "Show Featured" : "See More Latest"}
            </Button>
          </div>
        </PostsProvider>
      </div>
    </section>;
}